import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import orderBy from 'lodash/orderBy';
import JoinSkipButton from './JoinSkipButton';
import { DashlineSeparator, FlexContainer, SeparatorLine, StatusMessage, VerticalSeparator } from './styledElements';
import { useJoin } from '../../contexts/JoinContext';
import JoinPhotoUploadItem from './bulkUpload/JoinPhotoUploadItem';
import JoinActionButton from './JoinActionButton';
import Optional from '../../atoms/Optional';
import { handleJoinVysionEvent } from '../../utils/vysion/join';
import { VYSION_ACTIONS } from '../../utils/vysion';
import { JoinClickVysionEventsEnum } from '../../utils/vysion/join/click';
import { EVENT_NAMES } from '../../constants/mixpanel';
import { PhotoFileType, PhotoListTypes } from '../../modules/gallery/constants';
import JoinEditablePhotoModal from './bulkUpload/JoinEditablePhotoModal';
import colors from '../../theme/colors';
import { pluralize } from '../../common';
import { useApp } from '../../contexts/AppContext';
import {
  ALL_MANDATORY_2PHOTOS,
  ALL_MANDATORY_3PHOTOS,
} from '../../utils/evidentlyFeatures/onboardingFieldsMandatory/common';
import { mixpanelTrackOnboardingCompleted } from '../../utils/mixpanel/onboardingCompleted';
import useMediaBreakpoints from '../../hooks/useMediaBreakpoints';
import { RootState } from '../../store';
import { mixpanelTrackEvent } from '../../utils/mixpanel/common';
import { getFileExtension, processFiles } from '../../utils/photoUpload';
import { getImage } from '../../modules/gallery/helpers';

interface JoinBulkPhotoUploadProps {
  isFetching: boolean;
}

const JoinBulkPhotoUpload: React.FC<JoinBulkPhotoUploadProps> = ({ isFetching }) => {
  const { isMobile } = useMediaBreakpoints();
  const {
    convertToFormData,
    setCurrentFormData,
    onFormSubmitted,
    prevStep,
    handleShowSkipModal,
    onBoardingMandatoryVariant,
    nextStep,
    uploadedPhoto,
    setUploadedPhoto,
    stepsFormData,
    skippedStepCounter,
    setSkippedStepCounter,
    currentStepIndex,
    isMandatoryField,
  } = useJoin();
  const [isError, setIsError] = useState(false);
  const [photos, setPhotos] = useState<Array<Blob | MediaSource | undefined>>([
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
    undefined,
  ]);

  const [hasUploadedPhoto, setHasUploadedPhoto] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fileList, setFileList] = useState<PhotoListTypes | null>(null);
  const [previewPhotos, setPreviewPhotos] = useState<PhotoListTypes>([]);
  const [activeFile, setActiveFile] = useState<PhotoFileType | null>(null);
  const [fileParseError, setFileParseError] = useState<string | null>(null);

  const { setModalState } = useApp();

  const canSkippedPhotos = !isMandatoryField();

  const uploadedPhotos = previewPhotos.filter(photo => typeof photo !== 'undefined');

  const [formData] = stepsFormData;
  const userIsAttractive = useMemo(() => formData.account_type === 2, [formData.account_type]);

  const amHasNotMetMandatoryPhotos = useMemo(() => {
    if (ALL_MANDATORY_2PHOTOS === onBoardingMandatoryVariant) {
      return uploadedPhotos.length < 2;
    }

    if (ALL_MANDATORY_3PHOTOS === onBoardingMandatoryVariant) {
      return uploadedPhotos.length < 3;
    }

    return uploadedPhotos.length < 1;
  }, [userIsAttractive, onBoardingMandatoryVariant, uploadedPhotos.length]);

  const styledDropzoneStylesSmallPicture = useMemo(
    () => ({
      paddingTop: 0,
      height: isMobile ? 107 : 101,
      minHeight: '135px !important;',
    }),
    [isMobile]
  );

  const styledDropzoneStyles = useMemo(
    () => ({
      paddingTop: isMobile ? 85 : 125,
      height: isMobile ? 220 : 207,
      minHeight: '276px !important;',
    }),
    [isMobile]
  );

  useEffect(() => {
    if (fileList) preparePhotoWorkerPhotos();
  }, [fileList]);

  const preparePhotoWorkerPhotos = async () => {
    try {
      setIsLoading(true);
      const processedResults = await processFiles(Array.from(fileList));

      const results = processedResults
        .filter(result => result.data !== null)
        .map((result, index) => ({
          edited: result.data,
          index: uuidv4(),
          raw: result.data,
          id: result.id,
          private: false,
          primary: index === 0,
          fileExtension: getFileExtension(result.id),
        }));

      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      setPreviewPhotos(prev => {
        return Array.from([...prev, ...results]);
      });

      const failedFiles = processedResults.filter(result => result.data === null).map(result => result.id);

      if (failedFiles.length > 0) {
        const formattedFailedFiles = failedFiles.join(', ');
        const errorMessage = `${"The following images couldn't be processed: "}${formattedFailedFiles}`;

        setFileParseError(errorMessage);
      }

      setIsLoading(false);
    } catch (error) {
      console.warn('error', error);
      setIsLoading(false);
      setFileParseError('An error occurred while processing the images. Please try again.');
    }
  };

  const updateFileLists = async (file: File) => {
    if (activeFile === null) {
      return;
    }

    const updatedActiveFile = { ...activeFile };

    updatedActiveFile.edited = (await getImage(file)) as string;
    setActiveFile(updatedActiveFile);

    const updatePreviewPhotos = previewPhotos.map(photo =>
      photo.index === activeFile.index ? updatedActiveFile : photo
    );

    setPreviewPhotos(updatePreviewPhotos);
  };

  const updatePrivatePrimaryFile = ({ isPrimary, isPrivate }) => {
    if (activeFile === null) return;

    const updatedActiveFile = { ...activeFile };

    updatedActiveFile.primary = isPrimary;
    updatedActiveFile.private = isPrivate;

    setActiveFile(updatedActiveFile);

    setPreviewPhotos(prevState => {
      const updatePreviewPhotos = prevState.map(photo =>
        photo.index === activeFile.index ? updatedActiveFile : { ...photo, primary: false }
      );

      return orderBy(updatePreviewPhotos, ['primary'], ['desc']);
    });
  };

  const onRemove = (selectedPhoto: PhotoFileType) => {
    const clonedPhotos = [...previewPhotos];
    const cleanPhotos = clonedPhotos.filter(photo => photo.index !== selectedPhoto.index);

    if (cleanPhotos.length > 0) {
      cleanPhotos[0].primary = true;
      cleanPhotos[0].private = false;
      cleanPhotos.slice(1).forEach(photo => {
        photo.primary = false;
      });
    }

    setPreviewPhotos(cleanPhotos);
    setFileList(null);
  };

  const onEdit = (file: PhotoFileType) => {
    setActiveFile(file);
  };

  useEffect(() => {
    const uploadedPhotos = previewPhotos.filter(photo => typeof photo !== 'undefined');

    setHasUploadedPhoto(uploadedPhotos.length > 0 && uploadedPhotos.length <= 20);
  }, [previewPhotos]);

  useEffect(() => {
    if (uploadedPhoto?.length > 0) {
      setPreviewPhotos(uploadedPhoto);
    }
    // setCurrentFormData({ photos: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedPhoto]);

  const onPhotoUpload = (photo: Blob, index) => {
    const currentPhotos = [...photos];

    currentPhotos.splice(index, 1, photo);

    setPhotos(currentPhotos);
    setCurrentFormData({ photos: previewPhotos });
  };

  const submit = (trackMixpanelCBAction: (hashId: string, isAccountIsMixpanelEligableEnabled?: boolean) => void) => {
    const formData = convertToFormData(false);

    onFormSubmitted(formData, true, trackMixpanelCBAction, previewPhotos, true, setUploadedPhoto);
  };

  const onSkipSubmit = (
    trackMixpanelCBAction: (hashId: string, isAccountIsMixpanelEligableEnabled?: boolean) => void
  ) => {
    const formData = convertToFormData(false);

    onFormSubmitted(formData, true, trackMixpanelCBAction, [], true);
  };

  const onSubmit = () => {
    if (amHasNotMetMandatoryPhotos) return;

    resetModalOverlay();
    setIsError(false);
    const filteredPhotos = previewPhotos.filter(photo => typeof photo !== 'undefined');

    if (filteredPhotos.length === 0) {
      setIsError(true);
      return;
    }

    mixpanelTrackEvent({
      eventName: EVENT_NAMES.PHOTO_STEP_COMPLETED,
      payload: {
        eventProperties: {
          Result: 'Completed',
          'Number of photos added': uploadedPhotos.length ?? 0,
        },
      },
    });

    submit((hashId, isAccountIsMixpanelEligableEnabled) => {
      if (hashId && isAccountIsMixpanelEligableEnabled) {
        mixpanelTrackOnboardingCompleted({
          skippedStep: skippedStepCounter
            ? Object.values(skippedStepCounter).filter(value => value === true).length
            : 0,
          formData: stepsFormData,
        });
      }
    });

    setUploadedPhoto(previewPhotos);
    nextStep(false);
  };

  const resetModalOverlay = () => {
    setModalState(prev => {
      return {
        ...prev,
        customOverlayStyle: { display: 'block' },
      };
    });
  };

  const onSkip = () => {
    handleJoinVysionEvent({
      action: VYSION_ACTIONS.CLICK,
      event: JoinClickVysionEventsEnum.CLICK_PHOTO_SKIP_YES_SKIP_FOR_NOW,
    });
    setIsError(false);

    setPhotos([undefined, undefined, undefined, undefined, undefined, undefined]);
    setPreviewPhotos([]);
    setCurrentFormData({ photos: [] });
    setHasUploadedPhoto(false);
    resetModalOverlay();

    mixpanelTrackEvent({
      eventName: EVENT_NAMES.PHOTO_STEP_COMPLETED,
      payload: {
        eventProperties: {
          Result: 'Skipped',
          'Number of photos added': 0,
        },
      },
    });

    if (isMandatoryField()) {
      handleShowSkipModal(() => {
        nextStep(false);
      });
    } else {
      setTimeout(() => {
        onSkipSubmit((hashId, isAccountIsMixpanelEligableEnabled) => {
          if (hashId && isAccountIsMixpanelEligableEnabled) {
            const skipCount = skippedStepCounter
              ? Object.values(skippedStepCounter).filter(value => value === true).length
              : 0;

            mixpanelTrackOnboardingCompleted({
              skippedStep: skipCount + 1,
              formData: stepsFormData,
            });
          }
        });
      }, 500);
    }
  };

  const photoUrl = (index: number) => {
    return photos[index] ? URL.createObjectURL(photos[index] as Blob) : undefined;
  };

  const previewPhotoUrl = (index: number) => {
    return previewPhotos[index] ? previewPhotos[index].edited : undefined;
  };

  return (
    <div>
      <Optional show={isError}>
        <StatusMessage status="error">
          <p style={{ paddingLeft: 5 }}>
            Please upload at least <span style={{ fontWeight: 600 }}>1 public photos.</span>
          </p>
        </StatusMessage>
      </Optional>
      {fileParseError && <StatusMessage status="error">{fileParseError} </StatusMessage>}

      {isLoading && (
        <div
          style={{
            color: colors.red[500],
            fontSize: 14,
            fontWeight: 600,
            margin: '20px 0',
          }}
        >
          Hang tight, we’re preparing your photos...
        </div>
      )}

      <div style={{ display: 'flex' }}>
        <div className="profile-pic" style={{ width: '70%' }}>
          <JoinPhotoUploadItem
            key={0}
            photoUrl={previewPhotoUrl(0) ? previewPhotoUrl(0) : photoUrl(0)}
            customStyledDropzoneStyles={{ ...styledDropzoneStyles, ...{ paddingTop: 0 } }}
            onPhotoUpload={photo => {
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              onPhotoUpload(photo, 0);
            }}
            customAddIconStyle={{
              height: 67.918,
              width: 76.408,
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%,-50%)',
            }}
            setFileList={setFileList}
            onRemove={() => onRemove(previewPhotos[0])}
            onEdit={() => {
              onEdit(previewPhotos[0]);
            }}
            isPrivate={previewPhotos[0]?.private ?? false}
            isLoading={isLoading}
            isSmallThumbnail={false}
            isMainProfile={true}
            countTotalPhotos={previewPhotos.length}
          />
        </div>
        <div className="other-pic" style={{ width: '35%' }}>
          <JoinPhotoUploadItem
            key={1}
            photoUrl={previewPhotoUrl(1) ? previewPhotoUrl(1) : photoUrl(1)}
            customStyledDropzoneStyles={styledDropzoneStylesSmallPicture}
            onPhotoUpload={photo => {
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              onPhotoUpload(photo, 1);
            }}
            customAddIconStyle={{
              height: 32.98,
              width: 37.102,
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%,-50%)',
            }}
            setFileList={setFileList}
            onRemove={() => onRemove(previewPhotos[1])}
            onEdit={() => onEdit(previewPhotos[1])}
            isPrivate={previewPhotos[1]?.private ?? false}
            isLoading={isLoading}
            isSmallThumbnail
            countTotalPhotos={previewPhotos.length}
          />
          <JoinPhotoUploadItem
            key={2}
            photoUrl={previewPhotoUrl(2) ? previewPhotoUrl(2) : photoUrl(2)}
            customStyledDropzoneStyles={styledDropzoneStylesSmallPicture}
            onPhotoUpload={photo => {
              // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
              onPhotoUpload(photo, 2);
            }}
            customAddIconStyle={{
              height: 32.98,
              width: 37.102,
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%,-50%)',
            }}
            setFileList={setFileList}
            onRemove={() => onRemove(previewPhotos[2])}
            onEdit={() => onEdit(previewPhotos[2])}
            isPrivate={previewPhotos[2]?.private ?? false}
            isLoading={isLoading}
            isSmallThumbnail
            countTotalPhotos={previewPhotos.length}
          />
        </div>
      </div>
      <div className="other-pic" style={{ display: 'flex' }}>
        <JoinPhotoUploadItem
          key={3}
          photoUrl={previewPhotoUrl(3) ? previewPhotoUrl(3) : photoUrl(3)}
          customStyledDropzoneStyles={styledDropzoneStylesSmallPicture}
          onPhotoUpload={photo => {
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            onPhotoUpload(photo, 3);
          }}
          customAddIconStyle={{
            height: 32.98,
            width: 37.102,
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%,-50%)',
          }}
          setFileList={setFileList}
          onRemove={() => onRemove(previewPhotos[3])}
          onEdit={() => onEdit(previewPhotos[3])}
          isPrivate={previewPhotos[3]?.private ?? false}
          isLoading={isLoading}
          isSmallThumbnail
          countTotalPhotos={previewPhotos.length}
        />
        <JoinPhotoUploadItem
          key={4}
          photoUrl={previewPhotoUrl(4) ? previewPhotoUrl(4) : photoUrl(4)}
          customStyledDropzoneStyles={styledDropzoneStylesSmallPicture}
          onPhotoUpload={photo => {
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            onPhotoUpload(photo, 4);
          }}
          customAddIconStyle={{
            height: 32.98,
            width: 37.102,
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%,-50%)',
          }}
          setFileList={setFileList}
          onRemove={() => onRemove(previewPhotos[4])}
          onEdit={() => onEdit(previewPhotos[4])}
          isPrivate={previewPhotos[4]?.private ?? false}
          isLoading={isLoading}
          isSmallThumbnail
          countTotalPhotos={previewPhotos.length}
        />
        <JoinPhotoUploadItem
          key={5}
          photoUrl={previewPhotoUrl(5) ? previewPhotoUrl(5) : photoUrl(5)}
          customStyledDropzoneStyles={styledDropzoneStylesSmallPicture}
          onPhotoUpload={photo => {
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            onPhotoUpload(photo, 5);
          }}
          customAddIconStyle={{
            height: 32.98,
            width: 37.102,
            position: 'absolute',
            left: '50%',
            top: '50%',
            transform: 'translate(-50%,-50%)',
          }}
          setFileList={setFileList}
          onRemove={() => onRemove(previewPhotos[5])}
          onEdit={() => onEdit(previewPhotos[5])}
          isPrivate={previewPhotos[5]?.private ?? false}
          isLoading={isLoading}
          isSmallThumbnail
          countTotalPhotos={previewPhotos.length}
        />
      </div>

      {previewPhotos.length > 6 &&
        (() => {
          const groupCount = Math.ceil((previewPhotos.length - 6) / 3);
          const items: JSX.Element[] = [];

          for (let groupIndex = 0; groupIndex < groupCount; groupIndex++) {
            const groupItems: JSX.Element[] = [];

            for (let itemIndex = 0; itemIndex < 3; itemIndex++) {
              const photoIndex = 6 + groupIndex * 3 + itemIndex;

              if (photoIndex < previewPhotos.length) {
                groupItems.push(
                  <JoinPhotoUploadItem
                    key={photoIndex}
                    photoUrl={previewPhotoUrl(photoIndex) || photoUrl(photoIndex)}
                    customStyledDropzoneStyles={styledDropzoneStylesSmallPicture}
                    onPhotoUpload={photo => {
                      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                      onPhotoUpload(photo, photoIndex);
                    }}
                    customAddIconStyle={{
                      height: 32.98,
                      width: 37.102,
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                    setFileList={setFileList}
                    onRemove={() => (previewPhotoUrl(photoIndex) ? onRemove(previewPhotos[photoIndex]) : undefined)}
                    onEdit={() => (previewPhotoUrl(photoIndex) ? onEdit(previewPhotos[photoIndex]) : undefined)}
                    isPrivate={previewPhotos[photoIndex]?.private ?? false}
                    isLoading={isLoading}
                    isSmallThumbnail
                    countTotalPhotos={previewPhotos.length}
                  />
                );
              } else {
                groupItems.push(
                  <JoinPhotoUploadItem
                    key={photoIndex}
                    containerStyle={
                      previewPhotos.length === 20
                        ? { visibility: 'visible', position: 'relative' }
                        : { visibility: 'hidden' }
                    }
                    photoItemStyle={{ visibility: 'hidden' }}
                    photoUrl={undefined}
                    customStyledDropzoneStyles={styledDropzoneStylesSmallPicture}
                    onPhotoUpload={() => undefined}
                    customAddIconStyle={{
                      height: 32.98,
                      width: 37.102,
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                    }}
                    isUploading={false}
                    setFileList={setFileList}
                    onRemove={() => undefined}
                    onEdit={() => undefined}
                    isLoading={false}
                    isSmallThumbnail
                    isMaxLength={previewPhotos.length === 20}
                    countTotalPhotos={0}
                  />
                );
              }
            }

            items.push(
              <div className="other-pic" style={{ display: 'flex' }} key={groupIndex}>
                {groupItems}
              </div>
            );
          }

          return items;
        })()}

      <DashlineSeparator />
      <JoinEditablePhotoModal
        isOpen={activeFile ? true : false}
        onReset={() => setActiveFile(null)}
        activeFile={activeFile}
        onClose={() => setActiveFile(null)}
        updateFileLists={updateFileLists}
        updatePrivatePrimaryFile={updatePrivatePrimaryFile}
      />
      <div style={{ textAlign: 'center' }}>
        <JoinActionButton
          label={
            isFetching
              ? 'Updating Profile...'
              : `Upload (${previewPhotos.filter(file => file).length}${previewPhotos.length > 20 ? '/20' : ''})  ${
                  !hasUploadedPhoto || isLoading
                    ? pluralize(previewPhotos.length, 'File', 'Files')
                    : isMandatoryField()
                      ? `${pluralize(previewPhotos.length, 'File', 'Files')}`
                      : `${pluralize(previewPhotos.length, 'File', 'Files')} & Finish`
                }`
          }
          customStyle={{ width: 'inherit', padding: '15px 22px' }}
          isDisabled={isFetching || !hasUploadedPhoto || isLoading || amHasNotMetMandatoryPhotos}
          onClick={onSubmit}
        />
        <Optional show={!isFetching}>
          <FlexContainer>
            <VerticalSeparator>
              <JoinSkipButton label="Back" onClick={() => prevStep()} />
              {canSkippedPhotos && (
                <>
                  <SeparatorLine />
                  <JoinSkipButton
                    label="Skip for now"
                    onClick={() => {
                      handleShowSkipModal(onSkip);
                      setSkippedStepCounter(prev => ({ ...prev, [currentStepIndex]: true }));
                    }}
                  />
                </>
              )}
            </VerticalSeparator>
          </FlexContainer>
        </Optional>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => {
  return {
    isFetching: state.auth.isFetching,
  };
};

export default connect(mapStateToProps, {})(JoinBulkPhotoUpload);
